// src/components/latest-results/UrineColorSelect.jsx
import { useEffect, useRef, useState } from "react";
import {
  URINE_COLOR_OPTIONS,
  getUrineColorOption,
} from "../../Utils/urineColors";

// Swatch dropdown for Urine Color. The technician sees the colour name next to
// a sample stripe; onChange receives the stored value (e.g. "PALE_YELLOW").
export default function UrineColorSelect({ value, onChange, disabled }) {
  const [isOpen, setIsOpen] = useState(false);
  const [dropUp, setDropUp] = useState(false);
  const containerRef = useRef(null);
  const buttonRef = useRef(null);
  const selected = getUrineColorOption(value);

  const PANEL_HEIGHT = 240; // matches max-h-60

  // Rows near the bottom of the scrolling test list open upwards instead.
  const toggleOpen = () => {
    setIsOpen((prev) => {
      if (prev) return false;
      const rect = buttonRef.current?.getBoundingClientRect();
      if (rect) {
        const spaceBelow = window.innerHeight - rect.bottom;
        setDropUp(spaceBelow < PANEL_HEIGHT && rect.top > spaceBelow);
      }
      return true;
    });
  };

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event) => {
      if (!containerRef.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setIsOpen(false);
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOpen]);

  const handleSelect = (option) => {
    onChange(option.value);
    setIsOpen(false);
  };

  return (
    <div ref={containerRef} className="relative w-full min-w-0">
      <button
        ref={buttonRef}
        type="button"
        disabled={disabled}
        onClick={toggleOpen}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        title={selected ? selected.label : "Select Color"}
        className="flex w-full items-center gap-2 rounded-md bg-[#D7D4F8] px-3 py-2 text-sm disabled:cursor-not-allowed disabled:opacity-60"
      >
        {/* swatch and chevron share a width so the label stays centred like the
            text inputs in the other rows */}
        <span
          className={`h-5 w-5 flex-none rounded border ${
            selected ? "border-black/20" : "border-dashed border-[#845BB3]/60"
          }`}
          style={selected ? { backgroundColor: selected.hex } : undefined}
        />
        <span
          className={`flex-1 truncate text-center ${
            selected ? "" : "text-gray-600"
          }`}
        >
          {selected ? selected.label : "Select Color"}
        </span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className={`h-5 w-5 flex-none text-[#845BB3] transition-transform ${
            isOpen ? "rotate-180" : ""
          }`}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>

      {isOpen && (
        <ul
          role="listbox"
          className={`absolute z-20 max-h-60 w-full min-w-[220px] overflow-y-auto rounded-md border border-[#D7D4F8] bg-white py-1 shadow-lg ${
            dropUp ? "bottom-full mb-1" : "mt-1"
          }`}
        >
          {URINE_COLOR_OPTIONS.map((option) => {
            const isSelected = selected?.value === option.value;
            return (
              <li key={option.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  onClick={() => handleSelect(option)}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm hover:bg-[#F1EEFF] ${
                    isSelected ? "bg-[#F1EEFF] font-semibold" : ""
                  }`}
                >
                  <span
                    className="h-5 w-7 flex-none rounded border border-black/20"
                    style={{ backgroundColor: option.hex }}
                  />
                  <span className="truncate">{option.label}</span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
